import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Generates page number array like: [1, 2, '...', 7, 8, 9, '...', 20]
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, '...', total);
  } else if (current >= total - 3) {
    pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }
  return pages;
}

export default function Pagination({ page, limit, total, onPageChange, onLimitChange }) {
  const totalPages = Math.ceil(total / limit);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  if (total === 0) return null;

  const btnBase = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--gray-200)',
    background: 'white', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500,
    fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s', color: 'var(--gray-800)',
    padding: '0 6px',
  };

  const btnActive = {
    ...btnBase,
    background: 'var(--purple-800)', color: 'white',
    border: '1.5px solid var(--purple-800)', fontWeight: 700,
  };

  const btnDisabled = {
    ...btnBase, opacity: 0.35, cursor: 'not-allowed',
  };

  const pageNums = getPageNumbers(page, totalPages);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: '4px 0 16px' }}>
      {/* Left: showing X–Y of Z */}
      <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
        Showing <strong style={{ color: 'var(--gray-700)' }}>{from}–{to}</strong> of <strong style={{ color: 'var(--gray-700)' }}>{total}</strong> results
      </div>

      {/* Center: page buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {/* Prev */}
        <button
          style={page === 1 ? btnDisabled : btnBase}
          onClick={() => page > 1 && onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft size={15} />
        </button>

        {pageNums.map((p, i) =>
          p === '...'
            ? <span key={`dot-${i}`} style={{ minWidth: 34, textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.82rem' }}>…</span>
            : <button
                key={p}
                style={p === page ? btnActive : btnBase}
                onClick={() => onPageChange(p)}
                onMouseEnter={e => { if (p !== page) { e.currentTarget.style.background = 'var(--purple-100)'; e.currentTarget.style.borderColor = 'var(--purple-400)'; e.currentTarget.style.color = 'var(--purple-800)'; } }}
                onMouseLeave={e => { if (p !== page) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.color = 'var(--gray-800)'; } }}
              >
                {p}
              </button>
        )}

        {/* Next */}
        <button
          style={page === totalPages ? btnDisabled : btnBase}
          onClick={() => page < totalPages && onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Right: per page selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--gray-400)' }}>
        <span>Per page:</span>
        <select
          value={limit}
          onChange={e => { onLimitChange(Number(e.target.value)); onPageChange(1); }}
          style={{ padding: '4px 10px', borderRadius: 8, border: '1.5px solid var(--gray-200)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', cursor: 'pointer', outline: 'none', color: 'var(--gray-800)', background: 'white' }}
        >
          {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}
